<template>
  <!-- As a row action, the row is the handshake and only the picture opens the player. -->
  <component
    :is="isRowAction ? 'div' : RouterLink"
    v-bind="isRowAction ? { role: 'button' } : { to: profilePath }"
    :class="[
      'attendee-row',
      {
        'is-handshake': isHandshake,
        'is-row-action': isRowAction,
        'is-blocked': player.isBlocked,
        'is-even': (order || 0) % 2 === 0,
      },
    ]"
    @click="isRowAction && emit('select')"
  >
    <div class="info">
      <component
        :is="isRowAction ? RouterLink : 'div'"
        v-bind="isRowAction ? { to: profilePath } : {}"
        class="avatar-wrapper"
        @click="isRowAction && $event.stopPropagation()"
      >
        <img v-if="player.profileImageBlobHash" class="picture" :src="getImageSrc" />
        <div v-else class="picture missing">
          <GamIcon :name="GamIconName.PAWN_EMPTY" :size="GamIconSize.SMALL" />
        </div>
        <div v-if="isUnpaired" class="unpaired-badge">
          <span>{{ localize('attendee.badge.unpaired') }}</span>
        </div>
      </component>

      <div class="text">
        <div class="checked-in" v-if="showCheckIn">
          <GamIcon :name="GamIconName.PIN" :size="GamIconSize.LARGE" is-primary />
        </div>

        <span v-if="player.username" class="username">@{{ player.username }}</span>
        <span v-if="!player.username && player.pseudonym" class="username">{{ player.pseudonym }}</span>
        <span v-if="isWaiting" class="status">Pending</span>
        <span v-else-if="isConfirmed" class="status confirmed">Confirmed</span>
        <span v-else-if="isWithdrawn" class="status">Withdrawn</span>
        <span v-else-if="isDeclined" class="status">Declined</span>
        <span v-if="player.id === user?.id" class="current-user">You</span>
      </div>
    </div>

    <div class="icons">
      <GamIcon v-if="isOrganizer" :name="GamIconName.OWNER" :size="GamIconSize.X_LARGE" is-primary />

      <div class="chess-set-count" v-if="chessSetCount">
        <GamIcon :name="GamIconName.CHESS_SET" :size="GamIconSize.X_LARGE" />
      </div>

      <div class="chips">
        <GamPlayerRoles
          v-if="player.playerTypes?.length && (showRoles || showCoach || showStreamer)"
          class="roles"
          :show-streamer="showStreamer"
          :show-coach="showCoach"
          :player="player"
        />

        <GamChip
          v-if="(false && handshakeCount > 0) || isHandshakePending"
          :label="String(handshakeCount)"
          :left-icon="GamIconName.HANDSHAKE"
          :size="GamChipSize.SMALL"
          :variant="isHandshakePending ? GamChipVariant.PRIMARY : GamChipVariant.DEFAULT"
          :is-active="Boolean(isHandshakeConfirmed || isHandshakePending)"
          is-upper-case
        />

        <GamChip
          v-if="typeof player.rating === 'number'"
          class="rating"
          :variant="GamChipVariant.DEFAULT"
          :left-icon="GamIconName.GAMBIT"
          :dots-rating="isDots ? player.rating : undefined"
          :label="isDots ? undefined : String(player.rating)"
          is-upper-case
        />
      </div>
    </div>
  </component>
</template>

<script setup lang="ts">
import type { AttendeeDto, HandshakeFields } from '@/core/data/user/user.interface';
import { RatingDisplayMode } from '@/core/data/user/user.type';
import { gambits, localize } from '@/core/gambits';
import { isPlayerUnpaired } from '@/core/helpers/unpaired.helper';
import { AppEndpoint } from '@/core/network/api/constants/api.url.constant';
import { useAuthStore } from '@/stores/auth.store';
import { useLiveHandshakesStore } from '@/stores/liveHandshakes.store';
import { GamChipSize, GamChipVariant } from '@/views/composables/constants/components/gamChip.constants';
import { GamIconName, GamIconSize } from '@/views/composables/constants/components/gamIcon.constants';
import { hasPlayedPosition } from '@/views/main/handshakes/utils/handshake-game';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { RouterLink } from 'vue-router';
import GamChip from '../GamChip.vue';
import GamIcon from '../GamIcon.vue';
import GamPlayerRoles from './GamPlayerRoles.vue';

interface AttendeeProps {
  player: AttendeeDto;
  isOrganizer?: boolean;
  isConfirmed?: boolean;
  isWithdrawn?: boolean;
  isDeclined?: boolean;
  isWaiting?: boolean;
  chessSetCount?: number;
  showRoles?: boolean;
  showCoach?: boolean;
  showStreamer?: boolean;
  isHandshake?: boolean;
  showCheckIn?: boolean;
  showUnpairedBadge?: boolean;
  order?: number;
  /** A tap on the row is the handshake or the game; only the picture opens the player. */
  isRowAction?: boolean;
}

const props = withDefaults(defineProps<AttendeeProps>(), {
  chessSetCount: 0,
  order: 0,
});

const emit = defineEmits<{ select: [] }>();

const profilePath = computed(() => `/@${props.player.username}?back=history`);

const authStore = useAuthStore();
const { user, userDetails } = storeToRefs(authStore);
const liveHandshakes = useLiveHandshakesStore();

// User info is fetched once, so a position played since then is read from the live handshakes.
const isUnpaired = computed(() => {
  if (!props.showUnpairedBadge) return false;
  return isPlayerUnpaired(props.player.id, {
    viewerId: user.value?.id,
    positionPartnerIds: userDetails.value?.positionPartnerIds,
    hasPlayedLivePosition: (playerId) => liveHandshakes.withPeer(playerId).some(hasPlayedPosition),
  });
});

const handshakeCount = computed(() => {
  return (props.player as any as HandshakeFields)?.handshakeCount || 0;
});

const isHandshakeConfirmed = computed(() => {
  return (props.player as any as HandshakeFields)?.isHandshakeConfirmed;
});

const isHandshakePending = computed(() => {
  return (props.player as any as HandshakeFields)?.isHandshakePending;
});

const isDots = computed(() => props.player?.ratingDisplayMode === RatingDisplayMode.DOTS);

const getImageSrc = computed((): string => {
  let url = `${gambits.configService.getServiceRoutingHost()}${AppEndpoint.BLOB}`;
  url = url.replace(new RegExp('{blobHash}', 'g'), props.player?.profileImageBlobHash || '');
  return `${url}?width=100`;
});
</script>

<style lang="scss" scoped>
@use '@/ui/css/partial';

@keyframes attention {
  0% {
    filter: none;
  }

  33% {
    filter: none;
  }

  50% {
    filter: saturate(0%) brightness(200%);
  }

  66% {
    filter: none;
  }

  100% {
    filter: none;
  }
}

.attendee-row {
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 56px;
  width: 100%;
  border-radius: 56px !important;
  padding: 4px 16px 4px 4px;
  gap: 1rem;

  text-decoration: none;
  color: var(--color-white-100);
}

.attendee-row.is-handshake {
  flex: 1;
  width: auto;
  min-width: 0;
}

.attendee-row.is-row-action {
  -webkit-tap-highlight-color: transparent;
}

a.avatar-wrapper {
  display: block;
}

.attendee-row:not(.is-blocked) {
  @extend %tinted-card;
}

.attendee-row.is-blocked {
  cursor: unset;
  @extend %disabled-card;
  opacity: 0.5;
}

.info {
  display: flex;
  align-items: center;
  justify-content: stretch;
  flex-grow: 1;
  gap: 0.75rem;
  overflow: hidden;
}

.icons {
  display: flex;
  align-items: center;
  justify-content: stretch;
  flex-shrink: 1;
  gap: 0.75rem;
}

.avatar-wrapper {
  position: relative;
  flex-shrink: 0;
  width: 48px;
  height: 48px;
}

.unpaired-badge {
  position: absolute;
  top: 2px;
  left: 50%;
  transform: translateX(-50%);
  background: var(--color-linear-gradient-10), color-mix(in srgb, var(--color-white) 5%, var(--color-dark-700));
  font-size: 6px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 1px 3px;
  border-radius: 2rem;
  white-space: nowrap;
  box-shadow: 0 2px 6px rgba(black, 0.4);
  z-index: 1;

  span {
    background: var(--color-linear-gradient);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    color: var(--color-pink);
  }
}

.picture {
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  object-fit: cover;
  overflow: hidden;
  border-radius: 48px !important;
  filter: saturate(0);

  &.missing {
    // background: var(--color-linear-gradient);
    background: var(--color-dark-70);
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

.text {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;
  overflow: hidden;
  transform: translateY(-1.5px);
}

.text > * {
  line-height: 21px;
}

.checked-in {
  margin-left: -0.125rem;
  animation: attention 2000ms infinite ease-in-out;
  animation-fill-mode: backwards;
}

// .is-even .checked-in {
//   animation-delay: 1000ms;
// }

.username {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 500;
  letter-spacing: 0.03em;
  font-size: 16px;
}

.current-user {
  color: var(--color-white-30);
  font-weight: 300;
  letter-spacing: 0.01em;
}

.status {
  color: var(--color-pink);
  font-weight: 400;
  letter-spacing: 0.01em;
}

.status.confirmed {
  color: var(--color-white-30);
}

.chess-set-count {
  display: flex;
  align-items: center;
}

.chips,
.chips .roles {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
}

.is-blocked {
  // opacity: 0.5;
  pointer-events: none;
}
</style>
